import type { BuilderCourseCategory, DifficultyLevel } from '@/types';
import { inputClassName, labelClassName } from './constants';

const CATEGORIES: { value: BuilderCourseCategory; label: string }[] = [
  { value: 'induction', label: 'Inducción' },
  { value: 'technical', label: 'Técnica' },
  { value: 'soft_skills', label: 'Habilidades blandas' },
  { value: 'compliance', label: 'Cumplimiento' },
  { value: 'reinforcement', label: 'Reforzamiento' },
];

const DIFFICULTIES: { value: DifficultyLevel; label: string }[] = [
  { value: 'beginner', label: 'Principiante' },
  { value: 'intermediate', label: 'Intermedio' },
  { value: 'advanced', label: 'Avanzado' },
];

export type CourseSettingsFieldsProps = {
  category: BuilderCourseCategory | '';
  setCategory: (v: BuilderCourseCategory | '') => void;
  difficultyLevel: DifficultyLevel | '';
  setDifficultyLevel: (v: DifficultyLevel | '') => void;
  passingGrade: number;
  setPassingGrade: (v: number) => void;
  maxAttempts: number;
  setMaxAttempts: (v: number) => void;
  durationValue: number | '';
  setDurationValue: (v: number | '') => void;
  durationUnit: string;
  setDurationUnit: (v: string) => void;
  estimatedHours: number | '';
  setEstimatedHours: (v: number | '') => void;
  trainingValidity: number | '';
  setTrainingValidity: (v: number | '') => void;
  minVideoPercent: number | '';
  setMinVideoPercent: (v: number | '') => void;
  isRequired: boolean;
  setIsRequired: (v: boolean) => void;
  sequentialUnlock: boolean;
  setSequentialUnlock: (v: boolean) => void;
};

export function CourseSettingsFields({
  category,
  setCategory,
  difficultyLevel,
  setDifficultyLevel,
  passingGrade,
  setPassingGrade,
  maxAttempts,
  setMaxAttempts,
  durationValue,
  setDurationValue,
  durationUnit,
  setDurationUnit,
  estimatedHours,
  setEstimatedHours,
  trainingValidity,
  setTrainingValidity,
  minVideoPercent,
  setMinVideoPercent,
  isRequired,
  setIsRequired,
  sequentialUnlock,
  setSequentialUnlock,
}: CourseSettingsFieldsProps) {
  return (
    <>
      {/* Category + Difficulty */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="course-category" className={labelClassName}>
            Categoría
          </label>
          <select
            id="course-category"
            value={category}
            onChange={(e) => setCategory(e.target.value as BuilderCourseCategory | '')}
            className={inputClassName}
          >
            <option value="">Sin categoría</option>
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="course-difficulty" className={labelClassName}>
            Dificultad
          </label>
          <select
            id="course-difficulty"
            value={difficultyLevel}
            onChange={(e) => setDifficultyLevel(e.target.value as DifficultyLevel | '')}
            className={inputClassName}
          >
            <option value="">Sin especificar</option>
            {DIFFICULTIES.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Passing grade + Max attempts */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="course-passing" className={labelClassName}>
            Nota aprobatoria (%)
          </label>
          <input
            id="course-passing"
            type="number"
            min={0}
            max={100}
            value={passingGrade}
            onChange={(e) => setPassingGrade(Number(e.target.value))}
            className={inputClassName}
          />
        </div>
        <div>
          <label htmlFor="course-attempts" className={labelClassName}>
            Intentos máximos
          </label>
          <input
            id="course-attempts"
            type="number"
            min={1}
            max={10}
            value={maxAttempts}
            onChange={(e) => setMaxAttempts(Number(e.target.value))}
            className={inputClassName}
          />
        </div>
      </div>

      {/* Duration */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="course-duration" className={labelClassName}>
            Duración
          </label>
          <input
            id="course-duration"
            type="number"
            min={1}
            max={365}
            value={durationValue}
            onChange={(e) => setDurationValue(e.target.value ? Number(e.target.value) : '')}
            className={inputClassName}
            placeholder="Opcional"
          />
        </div>
        <div>
          <label htmlFor="course-duration-unit" className={labelClassName}>
            Unidad
          </label>
          <select
            id="course-duration-unit"
            value={durationUnit}
            onChange={(e) => setDurationUnit(e.target.value)}
            className={inputClassName}
          >
            <option value="days">Días</option>
            <option value="weeks">Semanas</option>
            <option value="months">Meses</option>
          </select>
        </div>
      </div>

      {/* Estimated hours + Training validity */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="course-hours" className={labelClassName}>
            Horas estimadas
          </label>
          <input
            id="course-hours"
            type="number"
            min={1}
            max={9999}
            value={estimatedHours}
            onChange={(e) => setEstimatedHours(e.target.value ? Number(e.target.value) : '')}
            className={inputClassName}
            placeholder="Opcional"
          />
        </div>
        <div>
          <label htmlFor="course-validity" className={labelClassName}>
            Vigencia (meses)
          </label>
          <input
            id="course-validity"
            type="number"
            min={1}
            max={120}
            value={trainingValidity}
            onChange={(e) => setTrainingValidity(e.target.value ? Number(e.target.value) : '')}
            className={inputClassName}
            placeholder="Opcional"
          />
        </div>
      </div>

      {/* Min video percent */}
      <div>
        <label htmlFor="course-video-pct" className={labelClassName}>
          % mínimo de video
        </label>
        <input
          id="course-video-pct"
          type="number"
          min={1}
          max={100}
          value={minVideoPercent}
          onChange={(e) => setMinVideoPercent(e.target.value ? Number(e.target.value) : '')}
          className={inputClassName}
          placeholder="Opcional (ej: 80)"
        />
      </div>

      {/* Checkboxes */}
      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={isRequired}
            onChange={(e) => setIsRequired(e.target.checked)}
            className="rounded border-input"
          />
          Obligatoria
        </label>
        <label className="flex items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={sequentialUnlock}
            onChange={(e) => setSequentialUnlock(e.target.checked)}
            className="rounded border-input"
          />
          Desbloqueo secuencial
        </label>
      </div>
    </>
  );
}
